import { Snackbar } from "@mui/material";
import { Alert } from "@mui/material";
import BuisyIndator from "SandboxViewerComponent/src/libs/BuisyIndicator";
import { autorun, runInAction } from "mobx";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import RootStore from "SandboxViewerComponent/src/stores/RootStore";
import { REGIONAL_SELECTION_DEFAULTS } from "configs/config";
// import { getTimespansFromCommunicationStore } from "utils/possibilities";
/**
 *
 * @param {object} CommunicationStore vom Sandboxviewer
 * @param {string} attributeId welches im Klimaslider angezeigt werden soll
 * @returns {string} attributeId
 */

const hasExtent = (urlParams) => {
  return (
    urlParams.has("xmin") &&
    urlParams.has("xmax") &&
    urlParams.has("ymin") &&
    urlParams.has("ymax")
  );
};

const hierarchy = [
  "Kategorie",
  "Parameter",
  "Zeitabschnitt",
  "Zeitraum",
  "Szenario",
  "Characteristic",
];

const hierarchyMap = {
  Kategorie: 0,
  Parameter: 1,
  Zeitabschnitt: 2,
  Zeitraum: 3,
  Szenario: 4,
  Characteristic: 5,
};

let savedState = {};

// URL values are numeric indices. During startup, child choices can briefly be
// stale while parent filter values are still settling, so confirm an apparent
// out-of-bounds index before treating an old shared link as only partially valid.
const INVALID_RESTORE_CONFIRM_DELAY_MS = 500;

const readFilterAccess = (CommunicationStore) => {
  return hierarchy.map((level) => {
    const accessor = CommunicationStore.getMessagesToID({
      to: "filterAccess",
    })?.message(level);
    if (accessor) {
      const { items, index, setIndex, visible } = accessor;
      return { level, items, setIndex, index, accessor, visible };
    } else {
      return { level };
    }
  });
};

const isValidRestoreIndex = (value) => {
  return Number.isInteger(value) && value >= 0;
};

const canApplyRestoreValue = (access, value) => {
  if (!access || access.visible === false || !access.setIndex) return false;
  if (!isValidRestoreIndex(value)) return false;

  const items = Array.isArray(access.items) ? access.items : [];
  return value < items.length;
};

const getInvalidRestoreIndices = (filterAccess, filterValues) => {
  if (!filterValues) return [];

  const invalid = [];
  let parentStillRestoring = false;

  hierarchy.forEach((level) => {
    if (!Object.prototype.hasOwnProperty.call(filterValues, level)) return;
    if (parentStillRestoring) return;

    const value = filterValues[level];
    const access = filterAccess[hierarchyMap[level]];

    if (access?.index === value) return;

    const items = Array.isArray(access?.items) ? access.items : [];
    const hasChoices = items.length > 0;

    if (isValidRestoreIndex(value) && hasChoices && value >= items.length) {
      invalid.push({
        level,
        value,
        reason: "out-of-bounds",
        available: items.length,
      });
      parentStillRestoring = true;
      return;
    }

    parentStillRestoring = true;
  });

  return invalid;
};

const getRestoreWarningText = (invalidRestoreIndices) => {
  const invalidLevels = [
    ...new Set(invalidRestoreIndices.map(({ level }) => level)),
  ];

  if (invalidLevels.length === 1 && invalidLevels[0] === "Zeitraum") {
    return "Der aufgerufene Link konnte nicht vollst\u00e4ndig wiederhergestellt werden: Die gespeicherte Zeitraum-Auswahl passt nicht mehr zur aktuellen Konfiguration. Die Anwendung hat deshalb automatisch den letzten verf\u00fcgbaren Beobachtungszeitraum ausgew\u00e4hlt und die URL aktualisiert. Bitte pr\u00fcfen Sie die Zeitraum-Auswahl.";
  }

  return `Der aufgerufene Link konnte nicht vollst\u00e4ndig wiederhergestellt werden: Die gespeicherte Auswahl (${invalidLevels.join(
    ", "
  )}) passt nicht mehr zur aktuellen Konfiguration. Die Anwendung hat die URL aktualisiert. Bitte pr\u00fcfen Sie die Auswahl.`;
};

const setOperationalLayer = (
  values,
  CommunicationStore,
  hierarchyLevel = 0
) => {
  return new Promise((resolve, reject) => {
    const next = () => {
      if (hierarchyLevel + 1 === hierarchy.length) {
        resolve();
      }
      setOperationalLayer(values, CommunicationStore, hierarchyLevel + 1)
        .then((r) => resolve(r))
        .catch((e) => reject(e));
    };
    const retryOrCheck = () => {
      // wait and retry
      setTimeout(() => {
        setOperationalLayer(values, CommunicationStore, hierarchyLevel)
          .then((r) => resolve(r))
          .catch((e) => reject(e));
      }, 100);
    };
    const level = hierarchy[hierarchyLevel];
    if (values[level] === undefined) {
      next();
    } else {
      const accessor = CommunicationStore.getMessagesToID({
        to: "filterAccess",
      })?.message(level);
      if (!accessor) {
        retryOrCheck();
      } else {
        const { index, setIndex /*items, visible*/ } = accessor;
        if (index !== undefined && index === values[level] /*|| !visible*/) {
          next();
        } else {
          setIndex(values[level]);
          retryOrCheck();
        }
      }
    }
  });
};

const applyHistoryItem = (item, CommunicationStore) => {
  const promise = new Promise((resolve, reject) => {
    // set extent
    const extent = RootStore.mapStore.mapView.extent;
    extent.xmin = parseFloat(item.xmin);
    extent.xmax = parseFloat(item.xmax);
    extent.ymin = parseFloat(item.ymin);
    extent.ymax = parseFloat(item.ymax);

    // set opacity

    CommunicationStore.addMessage({
      to: "operationalOpacity",
      message: parseFloat(item.opacity),
    });

    // set geometrical restriction
    if (item.source && item.entity) {
      CommunicationStore.addMessage({
        to: "selectionGraphic",
        message: {
          entity: item.entity,
          source: item.source,
          from: "urlParams",
        },
      });
    } else {
      CommunicationStore.addMessage({
        to: "selectionGraphic",
        message: {
          entity: null,
          source: null,
          from: "urlParams",
        },
      });
    }

    // set basemap
    if (item.basemap) {
      const basemapGallery = RootStore.mapStore.basemapGallery;
      const basemaps = basemapGallery.source.basemaps.items;
      for (let i = 0; i < basemaps.length; i++) {
        if (basemaps[i].title === item.basemap) {
          basemapGallery.activeBasemap = basemaps[i];
          break;
        }
      }
    }

    //set layers
    if (item.layers) {
      const layerList = RootStore.additionalLayerStore?.store?.layers;
      if (Array.isArray(layerList)) {
        const layerUniques = item.layers.split("|");
        for (let i = 0; i < layerList.length; i++) {
          let layer = layerList[i];
          if (layerUniques.indexOf(layer.unique) !== -1) {
            layer.addToMap(RootStore.mapStore.mapView.map);
            layer.setChecked(true);
          } else {
            layer.removeFromMap(RootStore.mapStore.mapView.map);
            layer.setChecked(false);
          }
        }
      } else {
        console.warn(
          "additionalLayerStore not ready; skipping history layer apply"
        );
      }
    }

    // set operational layer
    setOperationalLayer(item, CommunicationStore, 0)
      .then(() => {
        resolve();
      })
      .catch((e) => reject(e));
  });
  return promise;
};

const reactToChange = (
  newState,
  oldState,
  changedKeys,
  filterAccess,
  url,
  HistoryLimit = 10
) => {
  let history = RootStore.history ? [...RootStore.history] : [];
  let lastentry = history.length > 0 ? history[history.length - 1] : null;
  const changes = {};
  const labels = {};
  if (changedKeys.length === 1 && changedKeys[0] === "hasLayer") {
    // this state should never happen again
    return false;
  }
  changedKeys.forEach((key) => {
    changes[key] = { old: oldState[key], new: newState[key] };
  });

  let newHistoryEntry = false;
  if (
    "xmin" in changes ||
    "xmax" in changes ||
    "ymin" in changes ||
    "ymax" in changes
  ) {
  }

  if ("opacity" in changes) {
  }

  if ("layers" in changes) {
    // newHistoryEntry = true;
  }

  if ("basemap" in changes) {
    newHistoryEntry = true;
  }

  if ("entity" in changes || "source" in changes) {
    newHistoryEntry = true;
  }

  // if (changedKeys)
  hierarchy.forEach((level) => {
    if ((!lastentry || lastentry.hasLayer) && level in changes) {
      newHistoryEntry = true;
    }

    if (level in newState) {
      const index = newState[level];

      for (let i = 0; i < filterAccess.length; i++) {
        const access = filterAccess[i];
        if (access.level === level) {
          labels[level] = access.items?.[index]?.label;
          break;
        }
      }
      if (!labels[level]) {
        labels[level] = " / ";
      }
    }
  });

  const state = { ...newState, labels, url };
  if (newHistoryEntry) {
    if (history.length >= HistoryLimit) {
      history.splice(0, history.length - HistoryLimit + 1); // delete first elements until only HistoryLimit - 1 are left
    }
    // create new entry
    history.push(state);
  } else {
    // update last entry
    /*let lastEntry = */ history.pop();
    history.push(state);
  }
  runInAction(() => { RootStore.history = history; });
  // window.rs = RootStore;
  return true;
};

// const applyState = (state) => {};

const setupApplication = (urlParams, CommunicationStore, RootStore) => {
  if (hasExtent(urlParams)) {
    RootStore.mapStore.mapView.extent = {
      xmin: parseFloat(urlParams.get("xmin")),
      xmax: parseFloat(urlParams.get("xmax")),
      ymin: parseFloat(urlParams.get("ymin")),
      ymax: parseFloat(urlParams.get("ymax")),
      spatialReference: {
        wkid: RootStore.mapStore.mapView.spatialReference.wkid,
      },
    };
  }
};

// const extentEqual = (urlParams, extent) => {
//   if (hasExtent(urlParams)) {
//     return (
//       urlParams.get("xmin") === `${extent?.xmin}` &&
//       urlParams.get("xmax") === `${extent?.xmax}` &&
//       urlParams.get("ymin") === `${extent?.ymin}` &&
//       urlParams.get("ymax") === `${extent?.ymax}`
//     );
//   } else {
//     return false;
//   }
// };

let extentTimeout = null;
let previousState = null;

// on start set hash, if no hash is given and a last state was saved
// if this behavior isnt what is wanted => just add a hash with at least the '#' and at least 1 more charackter, but it can be as long as desired
// eslint-disable-next-line no-restricted-globals
if (location.hash?.length <= 1) {
  // first charackter is '#' and should be ignored because even Browsers ignore it sometimes if only '#' is present, at least chrome does so
  // previousState = localStorage.getItem("paramState");
  if (previousState) {
    // eslint-disable-next-line no-restricted-globals
    location.hash = previousState;
  }
} else if (
  // eslint-disable-next-line no-restricted-globals
  !(location.hash.toLowerCase().indexOf("reset") in { 0: 1, 1: 1 }) &&
  // eslint-disable-next-line no-restricted-globals
  location.hash.toLowerCase().indexOf("staticlink") === -1
) {
  // eslint-disable-next-line no-restricted-globals
  previousState = location.hash;
}

/**
 * True only when the URL hash contains actual application state to restore
 * (a real deep-link or shared URL). False for an empty hash or the special
 * "#reset" value. Used to skip the loading indicator when there is nothing
 * to restore, so the start screen is immediate.
 */
// eslint-disable-next-line no-restricted-globals
const _hashHasStateToRestore = (() => {
  // eslint-disable-next-line no-restricted-globals
  const h = location.hash;
  if (!h || h.length <= 1) return false; // no hash at all
  if (h.toLowerCase().indexOf("reset") !== -1) return false; // explicit reset
  return true; // actual deep-link state
})();

const URLParameterController = (props) => {
  const { CommunicationStore, HistoryLimit } = props;

  // const [snackbarOpened, setSnackbarOpened] = useState(!!previousState);
  const [urlParams, setUrlParams] = useState(null);
  const [initedMap, setInitedMap] = useState(false);
  // const [hasOperationalLayer, setHasOperationalLayer] = useState(false);

  // all single parameters as state, cause otherwise rerender is not triggered
  const [extent, setExtent] = useState(null);

  const [layers, setLayers] = useState("");

  const [initialFilterAccessString, setInitialFilterAccessString] =
    useState("");
  const [initializedFilterAccess, setInitializedFilterAccess] = useState(false);
  const [initializedFilterValues, setInitializedFilterValues] = useState(false);

  const [filterValues, setFilterValues] = useState(null);
  const [opacity, setOpacity] = useState(null);
  const [opacityInitialized, setOpacityInitialized] = useState(false);

  // Start with loading=true only when there is real state to restore from the URL.
  // For a fresh start (no hash) or an explicit #reset, skip the indicator entirely.
  const [loading, setLoading] = useState(_hashHasStateToRestore);
  const [restoreWarning, setRestoreWarning] = useState("");

  const initialized = !!urlParams;
  const filterAccess = readFilterAccess(CommunicationStore);

  const gotoHistory = CommunicationStore.getMessagesToID({
    to: "gotoHistory",
  })?.message;

  useEffect(() => {
    if (gotoHistory) {
      setLoading(true);
      const { history, item, index } = gotoHistory;
      CommunicationStore.addMessage({ to: "gotoHistory", message: null }); // clear request
      applyHistoryItem(item, CommunicationStore)
        .then(() => {
          runInAction(() => { RootStore.history = history.splice(0, index + 1); }); // +1 // +2
          setLoading(false);
        })
        .catch((e) => {
          console.error("an Error occurred during History redo", e);
          setLoading(false);
          if (item.url) {
            window.location = item.url;
          }
          //TODO: ggf mit item.url einfach nen reload ??
        });
    }
  }, [CommunicationStore, gotoHistory]);
  /*

  to: "gotoHistory",<BuisyIndator text={nls.calculating} open={loading} />
                            message: {
                              history: RootStore.history.concat([]), // copy of current history
                              index: itemIndex,
                              item,
                            },
  */

  const selectionGraphic = CommunicationStore.getMessagesToID({
    to: "selectionGraphic",
  })?.message;
  const selectionGraphicSource = selectionGraphic?.source;
  const selectionGraphicEntity = selectionGraphic?.entity;
  const selectionGraphicFrom = selectionGraphic?.from;

  // const operationalLayers = CommunicationStore.getMessagesToID({
  //   to: "shownLayers",
  // })?.message?.layerInfos?.map((i) => i.layer);
  // const hasOperationalLayer =
  //   !!operationalLayers?.[operationalLayers?.length - 1];
  /*
  message: {
      layerInfos: layerIds.map((info) => {
        //sende alle infos, die in der Konfig zu dem dargestellten layer stehen.
        return {
          layerId: info.id,
          layer: this.layers[info._internalID],
          metaData: info,
        };
      }),
    },
 */
  // const timespans = getTimespansFromCommunicationStore(CommunicationStore);

  // console.error("Timespans", timespans);
  useEffect(() => {
    if (urlParams && urlParams.has("debug")) {
      window.debugActive = true;
    }
  }, [urlParams]);

  // listen to basemapChanges
  const [basemapInitialized, setBasemapInitialized] = useState(false);
  const [basemap, setBasemap] = useState("");
  // initialize: wait for basemapArray and first set Basemap if existent in url
  useEffect(() => {
    const handles = [];
    if (RootStore.mapStore.basemapGallery && urlParams) {
      const basemapGallery = RootStore.mapStore.basemapGallery;
      if (basemapGallery) {
        let basemapArrayHandle = null;
        const apply = (source) => {
          if (source?.basemaps?.items?.length) {
            const basemaps = source.basemaps.items.map(
              (basemap) => basemap.title
            );
            if (urlParams.has("basemap")) {
              const index = basemaps.indexOf(urlParams.get("basemap"));
              if (index !== -1) {
                const basemap = source.basemaps.items[index];
                RootStore.mapStore.mapView.map.basemap = basemap;
                basemapGallery.activeBasemap = basemap;
                setBasemap(basemap.title);
              } else {
                setBasemap(basemapGallery.activeBasemap.title);
              }
            } else {
              setBasemap(basemapGallery.activeBasemap.title);
            }
            handles.push(
              basemapGallery.watch("activeBasemap", (basemap) => {
                setBasemap(basemap.title);
              })
            );
            setBasemapInitialized(true);
            const hindex = handles.indexOf(basemapArrayHandle);
            if (hindex !== -1) handles.splice(hindex, 1);
            basemapArrayHandle?.remove();
          }
        };
        if (basemapGallery.source?.basemaps?.items?.length) {
          apply(basemapGallery.source);
        } else basemapArrayHandle = basemapGallery.watch("source", apply);
        if (basemapArrayHandle) {
          handles.push(basemapArrayHandle);
        }
      }
    }
    return () => {
      handles.forEach((handle) => {
        try {
          handle.remove();
        } catch (e) {
          console.error(e);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RootStore.mapStore.basemapGallery, urlParams]);

  const filterAccessString = filterAccess
    .filter(({ visible, index }) => {
      return visible !== false && index !== -1;
    })
    .map(({ level, index }) => `${level}=${index}`)
    .join(",");
  const filterAccessInitialized =
    filterAccess.map((item) => !!item.setIndex).indexOf(false) === -1;

  const storeOpacity = CommunicationStore.getMessagesToID({
    to: "operationalOpacity",
  })?.message;

  // handle operational Layer opacity
  useEffect(() => {
    // urlParams will only be set once on startup
    if (
      urlParams &&
      urlParams.has("opacity") &&
      !isNaN(urlParams.get("opacity"))
    ) {
      // only apply opacity to operational layer if opacity and
      const opacity = parseFloat(urlParams.get("opacity"));
      if (!isNaN(opacity)) {
        CommunicationStore.addMessage({
          to: "operationalOpacity",
          message: opacity,
        });
        setOpacity(opacity);
      }
      setOpacityInitialized(true);
    } else if (urlParams && !urlParams.has("opacity")) {
      setOpacityInitialized(true);
    }
  }, [urlParams]);

  useEffect(() => {
    if (
      opacityInitialized &&
      (storeOpacity || storeOpacity === 0 || storeOpacity === 0.0) &&
      storeOpacity !== opacity
    ) {
      setOpacity(storeOpacity);
    }
  }, [storeOpacity, opacity, opacityInitialized]);

  // ##############################################################################################################
  // handle initialization of LayerFilterSelection
  useEffect(() => {
    if (initialized && filterAccessInitialized) {
      const values = {};
      filterAccess.forEach(({ level }) => {
        if (urlParams.has(level)) {
          const value = parseInt(urlParams.get(level));
          if (
            !isNaN(value) &&
            value >=
              0 /* && value < items.length */ /* items are maybe not the items for the given higher hierarchy cause it will be loaded async */
          ) {
            values[level] = value;
            // setIndex(value);
            // problem with async items => will be set later in the setTimeout
          }
          // else {
          //   if (index !== -1) values[level] = index;
          // }
        }
        // else {
        //   if (index !== -1) values[level] = index;
        // }
      });

      const tfilterAccessString = filterAccess
        .filter(({ level, visible }) => {
          return (
            values[level] !== undefined && values[level] !== -1 // &&
            // visible !== false
          );
        })
        .map(
          ({ level, index }) =>
            `${level}=${values[level] !== undefined ? values[level] : -1}`
        )
        .join(",");

      const zeitraumAccess = filterAccess[hierarchyMap.Zeitraum];
      const current = zeitraumAccess?.items?.[values["Zeitraum"]];
      let hasOperationalLayer = false;
      if (current) {
        const possibilities = current.possibilities.filter((possibility) => {
          const constraints = possibility.constraints;
          const keys = Object.keys(constraints);
          let fullfilled = true;
          for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const access = filterAccess[hierarchyMap[key]];
            if (
              access?.items &&
              access?.index !== undefined &&
              access.index !== -1
            ) {
              const item = access.items[access.index];
              if (item.label !== constraints[key]) {
                fullfilled = false;
                break;
              }
            }
          }
          return fullfilled;
        });
        // console.error("possibilities   x  ", possibilities);
        if (possibilities.length === 1 && possibilities[0].url) {
          hasOperationalLayer = true;
        }
      }

      values.hasLayer = hasOperationalLayer;
      setInitialFilterAccessString(tfilterAccessString);
      // setHasOperationalLayer(hasOperationalLayer);
      setFilterValues(values);
    }
  }, [initialized, filterAccessInitialized]);

  useEffect(() => {
    let timeout = null;
    let intervals = {};
    if (!initializedFilterAccess && filterValues) {
      const invalidRestoreIndices = getInvalidRestoreIndices(
        filterAccess,
        filterValues
      );

      if (invalidRestoreIndices.length > 0) {
        timeout = setTimeout(() => {
          const confirmedInvalidRestoreIndices = getInvalidRestoreIndices(
            readFilterAccess(CommunicationStore),
            filterValues
          );

          if (confirmedInvalidRestoreIndices.length > 0) {
            console.warn(
              "URLParameterController: ignoring non-restorable URL filter indices",
              confirmedInvalidRestoreIndices
            );
            setRestoreWarning(
              getRestoreWarningText(confirmedInvalidRestoreIndices)
            );
            setInitializedFilterAccess(true);
          }
        }, INVALID_RESTORE_CONFIRM_DELAY_MS);
      } else if (filterAccessString === initialFilterAccessString) {
        setInitializedFilterAccess(true);
      } else {
        timeout = setTimeout(() => {
          // console.warn("filterAccess", filterAccess);
          filterAccess.forEach((access) => {
            const { level, setIndex } = access;
            if (
              Object.prototype.hasOwnProperty.call(filterValues, level) &&
              canApplyRestoreValue(access, filterValues[level])
            ) {
              setIndex(filterValues[level]);
              intervals[level] = setInterval(() => {
                // visible
                const latestAccess = CommunicationStore.getMessagesToID({
                  to: "filterAccess",
                }).message(level);
                if (latestAccess?.index !== filterValues[level]) {
                  if (canApplyRestoreValue(latestAccess, filterValues[level])) {
                    latestAccess.setIndex(filterValues[level]);
                  } else {
                    clearInterval(intervals[level]);
                  }
                } else {
                  clearInterval(intervals[level]);
                }
              }, 100);
            }
          });
        }, 100);
      }
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      Object.keys(intervals).forEach((interval) =>
        clearInterval(intervals[interval])
      );
    };
  }, [
    filterAccessString,
    initialFilterAccessString,
    initializedFilterAccess,
    filterValues,
    filterAccess,
    CommunicationStore,
  ]);

  useEffect(() => {
    if (filterValues !== null && initializedFilterAccess) {
      const values = {};
      filterAccess
        .filter(({ visible, index }) => {
          return index !== -1 && visible !== false;
        })
        .forEach(({ level, index }) => {
          if (index !== -1) values[level] = index;
        });

      const zeitraumAccess = filterAccess[hierarchyMap.Zeitraum];
      const current = zeitraumAccess?.items?.[zeitraumAccess?.index];
      let hasOperationalLayer = false;
      if (current) {
        const possibilities = current.possibilities.filter((possibility) => {
          const constraints = possibility.constraints;
          const keys = Object.keys(constraints);
          let fullfilled = true;
          for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const access = filterAccess[hierarchyMap[key]];
            if (
              access?.items &&
              access?.index !== undefined &&
              access.index !== -1
            ) {
              const item = access.items[access.index];
              if (item.label !== constraints[key]) {
                fullfilled = false;
                break;
              }
            }
          }
          return fullfilled;
        });
        // console.error("possibilities   x  ", possibilities);
        if (possibilities.length === 1 && possibilities[0].url) {
          hasOperationalLayer = true;
        }
      }

      values.hasLayer = hasOperationalLayer;
      if (!initializedFilterValues) {
        setInitializedFilterValues(true);
        setLoading(false);
        setFilterValues(values);
      } else if (
        filterAccessString !==
        filterAccess
          .filter(({ visible, index }) => {
            return index !== -1 && visible !== false;
          })
          .map(
            ({ level, index }) =>
              `${level}=${
                filterValues[level] === undefined ? -1 : filterValues[level]
              }`
          )
          .join(",")
      )
        setFilterValues(values);
    }
  }, [filterValues, filterAccessString, initializedFilterAccess]);

  // ##############################################################################################################
  // handle map initialization and initial extent
  useEffect(() => {
    const handles = [];
    let removed = false;
    if (RootStore?.mapStore?.mapView) {
      handles.push(
        RootStore.mapStore.mapView.watch("extent", (xt) => {
          if (xt) {
            // const timeout = setTimeout(() => {
            setInitedMap(true);
            handles.forEach((handle) => handle.remove());
            removed = true;
            // }, 1000);
            // handles.push({
            //   remove: () => {
            //     clearTimeout(timeout);
            //   },
            // });
          }
        })
      );
    }
    return () => {
      if (!removed)
        handles.forEach((handle) => {
          try {
            handle.remove();
          } catch (e) {}
        });
    };
  }, [RootStore?.mapStore?.mapView]);

  // ##############################################################################################################
  // handle initialization
  useEffect(() => {
    if (initedMap) {
      let urlParams = null;
      // eslint-disable-next-line no-restricted-globals
      const fragment = location.hash;
      if (fragment.length > 0) {
        urlParams = new URLSearchParams(fragment.substring(1)); // ignore leading '#'
        // apply URLParams @ start of application
        setupApplication(urlParams, CommunicationStore, RootStore);
      } else {
        urlParams = new URLSearchParams();
      }

      // SINGLE SOURCE OF TRUTH:
      // URLParameterController is the SOLE owner of initialization
      // It ALWAYS sends the initial selection message with either:
      // 1. Values from URL params (if present)
      // 2. Default values from config (if URL params missing)
      // ZoomToGeo only responds to this message and user interactions

      const urlEntity =
        urlParams.get("entity") || REGIONAL_SELECTION_DEFAULTS.entity;
      const urlSource =
        urlParams.get("source") || REGIONAL_SELECTION_DEFAULTS.source;

      CommunicationStore.addMessage({
        to: "selectionGraphic",
        message: {
          entity: urlEntity,
          source: urlSource,
          from: "urlParams",
        },
      });

      setUrlParams(urlParams);
    }
  }, [initedMap, CommunicationStore]);

  // ##############################################################################################################
  // handle extent change
  useEffect(() => {
    const mapView = RootStore?.mapStore?.mapView;
    const handles = [];
    if (initialized && mapView) {
      if (mapView.extent) setExtent(mapView.extent);
      handles.push(
        mapView.watch("extent", (xt) => {
          if (
            xt &&
            (!extent ||
              xt.xmin !== extent.xmin ||
              xt.xmax !== extent.xmax ||
              xt.ymin !== extent.ymin ||
              xt.ymax !== extent.ymax)
          ) {
            if (extentTimeout) {
              clearTimeout(extentTimeout);
              extentTimeout = null;
            }
            extentTimeout = setTimeout(() => {
              setExtent(xt);
            }, 500);
          }
        })
      );
    }
    return () => {
      handles.forEach((handle) => handle.remove());
    };
  }, [initialized, RootStore?.mapStore?.mapView]);

  // ##############################################################################################################
  // handle additional Layers
  useEffect(() => {
    // Use MobX `autorun` to react when the layer list becomes available.
    // On startup `additionalLayerStore.store` can briefly be null, so we must
    // guard access to `store.layers` to avoid intermittent crashes.
    let disposer = () => {};
    let appliedUrlLayersOnce = false;

    const applyUrlLayers = (layerList) => {
      const raw = urlParams?.get("layers");
      if (!raw) return;

      const map = RootStore.mapStore?.mapView?.map;
      const uniques = raw.split("|").filter(Boolean);

      uniques.forEach((unique) => {
        const layer = layerList.find((l) => l.unique === unique);
        if (!layer) {
          console.warn(`Did not find Layer ${unique}`);
          return;
        }
        // If the map isn't ready yet, we'll retry on the next autorun.
        if (map) layer.addToMap(map);
        layer.setChecked(true);
      });
    };

    disposer = autorun(() => {
      const layerList = RootStore.additionalLayerStore?.store?.layers;
      if (!Array.isArray(layerList)) return;

      if (!appliedUrlLayersOnce && initialized && urlParams?.has("layers")) {
        if (RootStore.mapStore?.mapView?.map) {
          applyUrlLayers(layerList);
          appliedUrlLayersOnce = true;
        } else {
          // map not ready yet -> wait
          return;
        }
      }

      setLayers(
        layerList
          .filter((layer) => layer.checked)
          .map((layer) => layer.unique)
          .join("|")
      );
    });

    return () => disposer();
  }, [initialized, urlParams]);

  // ##############################################################################################################
  // handle URL Updates
  useEffect(() => {
    if (
      initialized &&
      basemapInitialized &&
      opacityInitialized &&
      initializedFilterValues
    ) {
      const state = {};
      let params = new URLSearchParams();
      // update extent
      if (extent) {
        params.set("xmin", `${extent.xmin}`);
        state.xmin = extent.xmin;
        params.set("xmax", `${extent.xmax}`);
        state.xmax = extent.xmax;
        params.set("ymin", `${extent.ymin}`);
        state.ymin = extent.ymin;
        params.set("ymax", `${extent.ymax}`);
        state.ymax = extent.ymax;
      }

      // update additional layers
      if (layers) {
        params.set("layers", layers);
        state.layers = layers;
      }
      // else {
      //   if (params.has("layers")) params.delete("layers");
      // }

      if (filterValues) {
        Object.keys(filterValues).forEach((level) => {
          if (level !== "hasLayer") params.set(level, filterValues[level]);
          state[level] = filterValues[level];
        });
      }

      if (opacity !== null) {
        params.set("opacity", opacity);
        state.opacity = opacity;
      }

      // eslint-disable-next-line eqeqeq
      if (basemap != "") {
        params.set("basemap", basemap);
        state.basemap = basemap;
      }

      if (selectionGraphicFrom) {
        if (selectionGraphicFrom === "zoomToGeo") {
          if (selectionGraphicSource && selectionGraphicEntity) {
            params.set("source", selectionGraphicSource.name);

            state.source = selectionGraphicSource.name;
            params.set(
              "entity",
              selectionGraphicEntity[selectionGraphicSource.displayAttribute]
            );

            state.entity =
              selectionGraphicEntity[selectionGraphicSource.displayAttribute];
          }
        } else if (selectionGraphicFrom === "urlParams") {
          params.set("source", selectionGraphicSource);

          state.source = selectionGraphicSource;
          params.set("entity", selectionGraphicEntity);
          state.entity = selectionGraphicEntity;
        }
      } else {
        if (urlParams?.has("source") && urlParams?.has("entity")) {
          params.set("source", urlParams.get("source"));
          state.source = urlParams.get("source");
          params.set("entity", urlParams.get("entity"));
          state.entity = urlParams.get("entity");
        }
      }

      // state.hasLayer = hasOperationalLayer; // wird nun über filterValues gesetzt

      const savedKeys = Object.keys(savedState);
      const keys = Object.keys(state);

      let last = "";
      const changedKeys = savedKeys
        .filter((key) => `${state[key]}` !== `${savedState[key]}`)
        .concat(keys.filter((key) => `${state[key]}` !== `${savedState[key]}`))
        .sort()
        .filter((key) => {
          const dif = key !== last;
          last = key;
          return dif;
        });
      if (changedKeys.length > 0 || state.hasLayer !== savedState.hasLayer) {
        // has any change
        if (
          reactToChange(
            state,
            savedState,
            changedKeys,
            filterAccess,
            // eslint-disable-next-line no-restricted-globals
            `${location.protocol}//${location.host}${location.pathname}${
              // eslint-disable-next-line no-restricted-globals
              location.search
            }#${params.toString()}&staticlink`,
            HistoryLimit
          )
        )
          savedState = state; // needed for getting state changes;
      }

      // update URL
      const hash = `#${params.toString()}`;
      // eslint-disable-next-line no-restricted-globals
      location.hash = hash;
      // localStorage.setItem("paramState", hash);
    }
  }, [
    initialized,
    extent,
    layers,
    filterValues,
    opacity,
    basemap,
    selectionGraphicEntity,
    selectionGraphicSource,
    selectionGraphicFrom,
    initializedFilterValues,
  ]);

  return (
    <div>
      {loading && (
        <div
          style={{
            pointerEvents: "none",
          }}
          data-cy="reset-busy-overlay"
        >
          <BuisyIndator
            open={true}
            text={
              "Anwendung wird auf den gewünschten Stand zurückgesetzt. Bitte haben sie einen Moment Geduld."
            }
            longLoadingText={"Anwendung neu starten"}
            longLoadingCallback={() => {
              localStorage.setItem("paramState", "");
              // eslint-disable-next-line no-restricted-globals
              location.hash = "reset";
              // eslint-disable-next-line no-restricted-globals
              location.reload();
            }}
          />
        </div>
      )}
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        open={!loading && !!restoreWarning}
        style={{ top: 75 }}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setRestoreWarning("");
        }}
      >
        <Alert
          severity="warning"
          onClose={() => {
            setRestoreWarning("");
          }}
        >
          {restoreWarning}
        </Alert>
      </Snackbar>
      {/* <Snackbar
        style={{ top: 75 }}
        open={!loading && snackbarOpened} // open it after loading indicator
        autoHideDuration={10000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          if (reason === "timeout") {
            setSnackbarOpened(false);
          }
          // TODO: close
        }}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <Alert
          severity="info"
          onClick={() => {
            // eslint-disable-next-line no-restricted-globals
            location.href = "#reset";
            // eslint-disable-next-line no-restricted-globals
            location.reload();
          }}
          onClose={(e) => {
            e.stopPropagation();
            setSnackbarOpened(false);
          }}
        >
          {
            "Der letzte Zustand der Anwendung wurde wiederhergestellt, wenn sie die Anwendung zurücksetzen wollen klicken sie auf diesen Text."
          }
        </Alert>
      </Snackbar> */}
    </div>
  );
};

export default observer(URLParameterController);
